import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from '@mui/material';
import { processChartText } from '../services/chartProcessingService';
import { AddVideoDetailsDialog } from './AddVideoDetailsDialog';
import { sheetsService } from '../services/googleSheetsService';
import { normalizeSongArtistKey } from '../utils/stringNormalization';

interface VideoManagerData {
  song?: string;
  artist?: string;
  youtubeLink?: string;
  voiceOverText?: string;
}

// Keep the Video interface simple for the new prop
interface Video {
  song: string;
  artist: string;
  youtubeLink?: string;
}

// Interface for the AddVideoDetailsDialog's expected video structure
interface VideoDetails {
    song: string;
    artist: string;
    thisWeek: number;
    lastWeek: number;
    weeks: number;
    peak: number;
    youtubeLink: string;
}

interface ChartUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (chartText: string) => void; 
  videoData: { [key: string]: VideoManagerData };
  onDetailsSave: (videos: Video[], chartText: string) => Promise<void>; 
}

export const ChartUploadDialog: React.FC<ChartUploadDialogProps> = ({
  open,
  onClose,
  onUpload, 
  videoData,
  onDetailsSave,
}) => {
  const [chartText, setChartText] = useState('');
  const [showAddVideoDetails, setShowAddVideoDetails] = useState(false);
  // State should hold the detailed structure needed by AddVideoDetailsDialog
  const [missingVideos, setMissingVideos] = useState<VideoDetails[]>([]); 

  const handleUploadClick = async () => {
    try {
      const entries = await processChartText(chartText);
      
      const normalizedVideoData = Object.entries(videoData).reduce((acc, [key, value]) => {
        const [song, artist] = key.split(' - ');
        const normalizedKey = normalizeSongArtistKey(song, artist);
        acc[normalizedKey] = value;
        return acc;
      }, {} as { [key: string]: VideoManagerData });
      
      const missing = entries.filter(entry => {
        const normalizedKey = normalizeSongArtistKey(entry.song, entry.artist);
        return !normalizedVideoData[normalizedKey];
      });

      if (missing.length > 0) {
        console.log('[ChartUploadDialog] Missing videos based on normalized key:', missing);
        
        // Map to the DETAILED structure needed by AddVideoDetailsDialog
        const videoDetails: VideoDetails[] = missing.map(entry => {
          const normalizedKey = normalizeSongArtistKey(entry.song, entry.artist);
          const existingData = Object.values(videoData).find(vd => normalizeSongArtistKey(vd.song || '', vd.artist || '') === normalizedKey);

          console.log(`[ChartUploadDialog] Processing missing entry: ${entry.song} - ${entry.artist} (Normalized: ${normalizedKey})`);
          if (existingData) {
            console.log(`  -> Found existing data via normalization: ${existingData.song} - ${existingData.artist}. Using these names.`);
            return {
              song: existingData.song || entry.song,
              artist: existingData.artist || entry.artist,
              youtubeLink: existingData.youtubeLink || '',
              // Add back the other fields needed by the dialog
              thisWeek: entry.position,
              lastWeek: entry.lastWeek,
              weeks: 1, // Defaulting to 1 for now.
              peak: entry.position,
            };
          } else {
            console.log(`  -> No existing data found even after normalization check. Using raw parsed names.`);
            return {
              song: entry.song,
              artist: entry.artist,
              youtubeLink: '',
              // Add back the other fields needed by the dialog
              thisWeek: entry.position,
              lastWeek: entry.lastWeek,
              weeks: 1,
              peak: entry.position,
            };
          }
        });

        console.log('[ChartUploadDialog] Video details being sent to AddVideoDetailsDialog:', videoDetails);
        setMissingVideos(videoDetails); // Set state with the detailed VideoDetails[] type
        setShowAddVideoDetails(true);
      } else {
        onUpload(chartText);
        onClose();
      }
    } catch (error) {
      console.error('Error processing chart:', error);
      // Optionally show error to user
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Upload Chart</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Chart Text"
              multiline
              rows={10}
              value={chartText}
              onChange={(e) => setChartText(e.target.value)}
              fullWidth
              placeholder="Paste chart text here..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleUploadClick} variant="contained">
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <AddVideoDetailsDialog
        open={showAddVideoDetails}
        onClose={() => {
           setShowAddVideoDetails(false);
        }}
        onSave={async (editedVideoDetails) => { 
          const videosToSave: Video[] = editedVideoDetails.map(details => ({
            song: details.song,
            artist: details.artist,
            youtubeLink: details.youtubeLink,
          }));
          await onDetailsSave(videosToSave, chartText); 
          setShowAddVideoDetails(false);
          onClose(); 
        }}
        videos={missingVideos} 
      />
    </>
  );
}; 